import { useContext, useEffect, useState } from 'react';

import type { Area, Country } from '@/types/regionTypes';
import { getLinkAriaLabel, getUrlText } from '@/utils/urlUtils';

import { Col } from '@/components/Col';
import { CountrySelector } from '@/components/CountrySelector';
import { Icon } from '@/components/Icon';
import { IconButton } from '@/components/IconButton';
import { LegacyButton } from '@/components/LegacyButton';
import { navToFindStores } from '@/components/Megamenu/helpers';
import { Row } from '@/components/Row';
import type {
  ContentfulFooterFragment,
  ContentfulMainMenuItemFragment,
} from '@/graphql/contentful/api';
import type { CountryCode, LanguageCodeEnum } from '@/graphql/saleor/api';
import { useWindowWidth } from '@/hooks/useWindowWidth';
import { getBrandConfig } from '@/lib/configuration/configuration';
import { getRegionList } from '@/lib/countries';
import { generateDataTestId } from '@/lib/tests';
import { LocaleContext } from '@/providers/LocaleProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import {
  countrySelectorActions,
  useCountrySelectorState,
} from '@/states/countrySelectorState';
import { theme } from '@/theme/getTheme';

import * as S from './Menu.styles';

export interface Item {
  id: string;
  internalName: string;
  label: string;
  url: string;
  visibleInDesktop: boolean;
  visibleInMobile: boolean;
}
export interface MenuItem {
  id: string;
  internalName: string;
  label: string;
  submenuItemsCollection: {
    items: Item[];
  };
  url: string;
  visibleInDesktop: boolean;
  visibleInMobile: boolean;
}

export type SocialName =
  | 'facebook'
  | 'instagram'
  | 'stava'
  | 'tiktok'
  | 'twitterX'
  | 'youtube'
  | 'pinterest';

export interface Social {
  link: string;
  name: SocialName;
}
export interface FooterDataProps {
  footerData: ContentfulFooterFragment;
}
export interface MenuProps {
  footerData: ContentfulFooterFragment;
  openDownloadCatalogPopup: () => void;
}
export interface LinksProps {
  copyRight: string;
  links: ContentfulMainMenuItemFragment[];
}

export interface MenuItemProps {
  item: ContentfulMainMenuItemFragment;
  openDownloadCatalogPopup: () => void;
}

export const LinkItem = ({ item, openDownloadCatalogPopup }: MenuItemProps) => {
  const url = getUrlText(item?.url ?? '');
  const locale = useContext(LocaleContext);

  const {
    country: { code },
  } = useRegionProvider();

  const openMenuItemPopup = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openDownloadCatalogPopup();
  };

  return (
    <S.LI data-test-id='LinkItem'>
      {item.type === 'Popup' ? (
        <S.FooterLinkButton
          aria-label={item.label ?? ''}
          onClick={openMenuItemPopup}
        >
          {item.label}
        </S.FooterLinkButton>
      ) : (
        <S.FooterLink
          aria-label={getLinkAriaLabel(url, item.label ?? '')}
          as={url === '/stores' ? 'a' : undefined}
          href={url === '/stores' ? navToFindStores(locale, code) : url}
          target={item?.openUrlInNewTab ? '_blank' : '_self'}
        >
          {item.label}
        </S.FooterLink>
      )}
    </S.LI>
  );
};

export const Links = ({ copyRight, links }: LinksProps) => {
  const copyRightWithYear = copyRight.replaceAll(
    '{year}',
    new Date().getFullYear().toString(),
  );

  const linksPosition = getBrandConfig().footerLinksPosition;

  return (
    <S.FooterBottomBox
      $footerLinksPosition={linksPosition}
      data-test-id='FooterBottom'
    >
      <S.BottomItemsWrapper data-test-id='Links'>
        {links?.map((item) => (
          <li
            key={item?.internalName}
            data-test-id={'LinkItem-' + item?.internalName}
          >
            <S.FooterBottomLink
              aria-label={getLinkAriaLabel(item?.url ?? '', item?.label || '')}
              href={getUrlText(item?.url ?? '')}
              target={item?.openUrlInNewTab ? '_blank' : '_self'}
            >
              {item?.label}
            </S.FooterBottomLink>
          </li>
        ))}
      </S.BottomItemsWrapper>

      <S.FooterVisibility
        $orderMobile={theme.components.footer.menus.orderMobile.break}
      >
        <S.Break />
      </S.FooterVisibility>

      <S.CopyRight $footerLinksPosition={linksPosition}>
        {copyRightWithYear}
      </S.CopyRight>
    </S.FooterBottomBox>
  );
};

export const Social = ({ footerData }: FooterDataProps) => {
  const socialItems = [
    { name: 'instagram' as SocialName, link: footerData.socialInstagramUrl },
    { name: 'stava' as SocialName, link: footerData.socialStravaUrl },
    { name: 'youtube' as SocialName, link: footerData.socialYoutubeUrl },
    { name: 'facebook' as SocialName, link: footerData.socialFacebookUrl },
    { name: 'tiktok' as SocialName, link: footerData.socialTiktokUrl },
    { name: 'twitterX' as SocialName, link: footerData.socialXTwitterUrl },
    { name: 'pinterest' as SocialName, link: footerData.socialPinterestUrl },
    { name: 'line' as SocialName, link: footerData.socialLineUrl },
  ].filter((item) => item.link);

  return (
    <Row data-test-id='Social'>
      <Col>
        <S.SocialIconBox flexWrap='wrap' justifyContent='left'>
          {socialItems?.map((social) => (
            <IconButton
              key={social.name}
              as='a'
              colorVariant='defaultInverted'
              href={getUrlText(social?.link ?? '')}
              iconProps={{ name: social.name, variant: 'regular' }}
              rel='noopener noreferrer'
              size='l'
              target='_blank'
              variant='ghosted'
            />
          ))}
        </S.SocialIconBox>
      </Col>
    </Row>
  );
};

export const MenuGroup = ({
  item,
  openDownloadCatalogPopup,
}: MenuItemProps) => {
  const resize = useWindowWidth();
  const [isShowSubmenu, setIsShowSubmenu] = useState<boolean>(false);
  const [isMobileFooter, setIsMobileFooter] = useState<boolean>(false);
  useEffect(() => {
    if ((resize ?? window.innerWidth) <= 1160) setIsMobileFooter(true);
    else setIsMobileFooter(false);
  }, [resize]);
  const handleOpenMenu = () => {
    if (isMobileFooter) {
      setIsShowSubmenu((prev) => !prev);
    }
  };

  return (
    <S.LI data-test-id='MenuGroup'>
      <S.FooterVisibility>
        <S.Break />
      </S.FooterVisibility>
      <S.MenuBox>
        <S.GroupTitleWrapper onClick={handleOpenMenu}>
          <S.GroupTitle>{item.label}</S.GroupTitle>
          {isMobileFooter && (
            <Icon
              name={isShowSubmenu ? 'chevronUp' : 'chevronDown'}
              size='l'
              variant='regular'
            />
          )}
        </S.GroupTitleWrapper>
        {(isShowSubmenu || !isMobileFooter) && (
          <S.UL>
            {item?.submenuItemsCollection?.items?.map(
              (subItem, i) =>
                !!subItem?.label && (
                  <LinkItem
                    key={subItem?.internalName + '_' + i}
                    item={subItem as ContentfulMainMenuItemFragment}
                    openDownloadCatalogPopup={openDownloadCatalogPopup}
                  />
                ),
            )}
          </S.UL>
        )}
      </S.MenuBox>
    </S.LI>
  );
};

export const Menu = ({ footerData, openDownloadCatalogPopup }: MenuProps) => {
  const { overlayStatus } = useCountrySelectorState();
  const regionsList = getRegionList();
  const { changeCountry, country, locale } = useRegionProvider();
  const [countryTabOpen, setCountryTabOpen] = useState(false);
  let currentCountry = country;
  const currentLanguage = locale;
  let currentArea = null;

  const onChangeLocale = async (
    countrySlug: CountryCode,
    languageSlug: LanguageCodeEnum,
  ) => {
    await changeCountry(countrySlug, languageSlug);
  };

  // Initialize area and country selector options
  const areas = regionsList?.map((a) => {
    const countries = a.countries?.map((c) => {
      return {
        ...c,
        label: c.name,
        value: c.slug,
        key: c.slug,
      };
    });

    return {
      ...a,
      countries,
    };
  }) as Area[];

  areas?.forEach((a) => {
    const country: Country | undefined = a.countries?.find(
      (c) => c.slug == currentCountry?.slug,
    );
    if (country) {
      currentArea = a;
      currentCountry = country!;
    }
  });

  const countryWithLangLabel =
    currentCountry?.name + ' - ' + currentLanguage?.name || '';

  const openChangeLocation = () => {
    countrySelectorActions.changeOverlayStatus('OPEN');
    setCountryTabOpen(true);
  };

  useEffect(() => {
    if (countryTabOpen) {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth',
      });
      setCountryTabOpen(false);
    }
  }, [countryTabOpen, setCountryTabOpen]);
  const itemLength = footerData.menuItemsCollection?.items?.length ?? 0;
  return (
    <>
      <S.GridBox itemCount={itemLength + 1}>
        {footerData.menuItemsCollection?.items
          ?.filter((item) => !!item)
          .map((item, idx) => (
            <MenuGroup
              key={item?.internalName + '_' + idx}
              item={item as ContentfulMainMenuItemFragment}
              openDownloadCatalogPopup={openDownloadCatalogPopup}
            />
          ))}
        <S.FooterVisibility>
          <S.Break />
        </S.FooterVisibility>
        <S.LI>
          <S.UL>
            <S.LI>
              <S.GroupTitle>{footerData.socialNetworkLabel}</S.GroupTitle>
              <Social footerData={footerData} />
              <S.FooterVisibility>
                <S.Break />
              </S.FooterVisibility>
            </S.LI>
            {footerData.displayRegionSelector && (
              <S.LI>
                <S.CountryRegion>
                  {footerData.regionSelectorLabel}
                </S.CountryRegion>
                <S.LanguageSelectorWrapper>
                  <LegacyButton
                    colorVariant='white'
                    data-test-id='FooterCountrySelectorButton'
                    label={countryWithLangLabel}
                    leftContent={
                      <Icon
                        name='globe'
                        size='l'
                        style={{ verticalAlign: 'middle', marginRight: '4px' }}
                        variant='regular'
                      />
                    }
                    onClick={() => openChangeLocation()}
                    variant='outlined'
                    {...generateDataTestId('footer', 'CountrySelectorButton')}
                  />
                </S.LanguageSelectorWrapper>
              </S.LI>
            )}
          </S.UL>
        </S.LI>
      </S.GridBox>
      {overlayStatus === 'OPEN' && currentArea && (
        <CountrySelector
          onClose={() => countrySelectorActions.changeOverlayStatus('CLOSE')}
          onContinue={(countrySlug, localeSlug) =>
            onChangeLocale(countrySlug, localeSlug)
          }
          regionsList={areas}
          userCountry={currentCountry}
          userLanguage={currentLanguage}
          userRegion={currentArea}
        />
      )}
    </>
  );
};
